"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { CreditCard, Plus, Pause, Users } from "lucide-react";

type CartState = "paying" | "adding" | "held";

type Cart = {
  name: { en: string; es: string };
  state: CartState;
  itemsCount: number;
  total: string;
  detail: { en: string; es: string };
  items: { name: { en: string; es: string }; qty: number; price: string }[];
};

const CARTS: Cart[] = [
  {
    name: { en: "Maria", es: "María" },
    state: "paying",
    itemsCount: 4,
    total: "$28.50",
    detail: { en: "Card · auth", es: "Tarjeta · autorizando" },
    items: [
      { name: { en: "Birria taco", es: "Taco birria" }, qty: 2, price: "$18.00" },
      { name: { en: "Agua fresca", es: "Agua fresca" }, qty: 1, price: "$4.00" },
      { name: { en: "Elote", es: "Elote" }, qty: 1, price: "$6.50" },
    ],
  },
  {
    name: { en: "John", es: "John" },
    state: "adding",
    itemsCount: 2,
    total: "$15.00",
    detail: { en: "Adding pastor", es: "Agregando pastor" },
    items: [
      { name: { en: "Pastor taco", es: "Taco pastor" }, qty: 2, price: "$15.00" },
    ],
  },
  {
    name: { en: "Walk-in #3", es: "Cliente #3" },
    state: "held",
    itemsCount: 3,
    total: "$22.00",
    detail: { en: "Holding · still deciding", es: "En espera · indecisos" },
    items: [
      { name: { en: "Quesabirria", es: "Quesabirria" }, qty: 1, price: "$12.00" },
      { name: { en: "Carnitas", es: "Carnitas" }, qty: 1, price: "$6.00" },
      { name: { en: "Jarrito", es: "Jarrito" }, qty: 1, price: "$4.00" },
    ],
  },
];

function stateMeta(state: CartState) {
  if (state === "paying") {
    return {
      tone: "#D7263D",
      bg: "rgba(215,38,61,0.10)",
      icon: CreditCard,
      label: { en: "Paying", es: "Cobrando" },
    };
  }
  if (state === "adding") {
    return {
      tone: "#F4722B",
      bg: "rgba(244,114,43,0.10)",
      icon: Plus,
      label: { en: "Adding", es: "Agregando" },
    };
  }
  return {
    tone: "#9B9690",
    bg: "rgba(155,150,144,0.10)",
    icon: Pause,
    label: { en: "Held", es: "En espera" },
  };
}

export default function RushHourCartsScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-[#FAFAF8] overflow-hidden">
      <div className="max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#D7263D" }}>
            {t("Multi-cart", "Multi-cart")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02] text-redy-charcoal">
            {t("Carts. ", "Carritos. ")}
            <span className="text-redy-charcoal/30">{t("Plural.", "Plural.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-redy-secondary leading-relaxed mt-6">
            {t(
              "Ring the next person while the card is authing. Take a third order while the first one is still deciding. Same iPad. Three open carts. Nobody waits because the screen is busy.",
              "Toma la siguiente orden mientras la tarjeta autoriza. Atiende un tercero mientras el primero todavía decide. Mismo iPad. Tres carritos abiertos. Nadie espera porque la pantalla esté ocupada."
            )}
          </p>
        </motion.div>

        {/* 3 cart cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 max-w-[1080px] mx-auto">
          {CARTS.map((c, i) => {
            const meta = stateMeta(c.state);
            const Icon = meta.icon;
            return (
              <motion.div
                key={c.name.en}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: 0.1 * i, ease: [0.21, 0.47, 0.32, 0.98] }}
                className="bg-white rounded-[24px] border overflow-hidden shadow-[0_10px_40px_rgba(0,0,0,0.04)]"
                style={{
                  borderColor: c.state === "paying" ? "rgba(215,38,61,0.30)" : "rgba(0,0,0,0.05)",
                  boxShadow: c.state === "paying"
                    ? "0 20px 50px rgba(215,38,61,0.12), 0 0 0 1px rgba(215,38,61,0.30)"
                    : "0 10px 40px rgba(0,0,0,0.04)",
                }}
              >
                {/* Cart header */}
                <div className="px-5 pt-5 pb-4 border-b border-black/5">
                  <div className="flex items-baseline justify-between mb-2">
                    <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60">
                      {t("Cart", "Cart")} {i + 1}
                    </div>
                    <div
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black tracking-wider uppercase"
                      style={{ backgroundColor: meta.bg, color: meta.tone }}
                    >
                      <Icon className="w-2.5 h-2.5" strokeWidth={3} />
                      {t(meta.label.en, meta.label.es)}
                    </div>
                  </div>

                  <div className="flex items-baseline justify-between">
                    <div className="text-[18px] font-black text-redy-charcoal">{t(c.name.en, c.name.es)}</div>
                    <div className="text-[20px] font-black tabular-nums text-redy-charcoal">{c.total}</div>
                  </div>
                  <div className="text-[11px] font-medium mt-1" style={{ color: meta.tone }}>
                    {t(c.detail.en, c.detail.es)}
                  </div>
                </div>

                {/* Cart body — items */}
                <div className="px-5 py-4 space-y-2 min-h-[140px]">
                  {c.items.map((it, ii) => (
                    <div key={ii} className="flex items-baseline justify-between text-[12px]">
                      <div className="text-redy-charcoal font-medium">
                        <span className="font-black tabular-nums mr-1">{it.qty}×</span>
                        {t(it.name.en, it.name.es)}
                      </div>
                      <div className="font-bold tabular-nums text-redy-charcoal/80">{it.price}</div>
                    </div>
                  ))}
                </div>

                {/* Cart footer — state-specific action */}
                <div className="px-5 py-4 border-t border-black/5 bg-[#FAFAF8]">
                  {c.state === "paying" && (
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-redy-secondary/60">
                          {t("Card auth", "Autorización")}
                        </div>
                        <div className="flex items-center gap-1.5 mt-1">
                          <motion.div
                            initial={{ scaleX: 0 }}
                            whileInView={{ scaleX: 1 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ duration: 1.2, delay: 0.4 }}
                            className="h-1.5 flex-1 rounded-full origin-left"
                            style={{ background: "linear-gradient(90deg, #F4722B 0%, #D7263D 100%)" }}
                          />
                          <div className="text-[10px] font-black tabular-nums text-redy-charcoal/60">
                            0.8s
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {c.state === "adding" && (
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-[11px] font-bold text-redy-charcoal">
                        {t("+ 1 item · still ringing", "+ 1 item · sigue cobrando")}
                      </div>
                      <div
                        className="w-7 h-7 rounded-full flex items-center justify-center"
                        style={{ background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)" }}
                      >
                        <Plus className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                      </div>
                    </div>
                  )}
                  {c.state === "held" && (
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-[11px] font-bold text-redy-charcoal/60">
                        {t("Tap to resume", "Tap para retomar")}
                      </div>
                      <div className="w-7 h-7 rounded-full flex items-center justify-center bg-redy-charcoal/10">
                        <Pause className="w-3.5 h-3.5 text-redy-charcoal/60" strokeWidth={3} />
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Below caption */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center mt-12"
        >
          <div className="inline-flex items-center gap-2 bg-white px-5 py-3 rounded-full border border-black/5 shadow-sm">
            <Users className="w-4 h-4" style={{ color: "#D7263D" }} />
            <span className="text-[13px] font-bold text-redy-charcoal">
              {t(
                "Ring while teammate cashes. Same iPad. Same shift.",
                "Cobra mientras tu compañero también cobra. Mismo iPad. Mismo turno."
              )}
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
